import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Download, Trash2, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { fetchExports, deleteExport } from "../../api/leaks";
import { toast } from "sonner";
import { Popover } from "../../components/feed-filters/Popover";

export default function ExportHistory() {
  const [exports, setExports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExports();
  }, []);

  const loadExports = async () => {
    setLoading(true);
    try {
      const res = await fetchExports();
      setExports(res);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteExport(id);
      setExports(prev => prev.filter(e => e._id !== id));
      toast.success("Export deleted");
    } catch (err) {
      toast.error("Failed to delete export");
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="leaks-page exports-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="leaks-inner">
        <Link to="/leaks" className="inline-flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 mb-6 transition-colors">
          <ArrowLeft size={16} /> Back to feed
        </Link>
        
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-white tracking-tight mb-2">Export History</h1>
            <p className="text-slate-400">Download previously generated intelligence reports and raw data.</p>
          </div>
          
          <Popover trigger={
            <button className="flex items-center gap-2 h-9 px-4 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors">
              <Plus size={16} /> New Export
            </button>
          }>
            <div className="p-2 w-48 flex flex-col">
              <button 
                onClick={() => toast.info("Go to the Intelligence Feed and click Export there to apply your current filters.")}
                className="text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded transition-colors"
              >
                Export Current Feed
              </button>
              <button 
                onClick={() => toast.info("Go to Saved Posts and click Export there.")}
                className="text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded transition-colors"
              >
                Export Saved Posts
              </button>
            </div>
          </Popover>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-2">
            {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-slate-800/30 rounded-lg"></div>)}
          </div>
        ) : exports.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-slate-700/50 rounded-xl">
            <FileText className="mx-auto text-slate-600 mb-4" size={32} />
            <h3 className="text-lg text-white font-medium mb-1">No export history</h3>
            <p className="text-sm text-slate-400">You haven't exported any data yet.</p>
          </div>
        ) : (
          <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Filename</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Rows</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {exports.map((exp) => (
                  <tr key={exp._id} className="hover:bg-slate-800/40 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                          <FileText size={16} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-200">{exp.filename}</p>
                          <p className="text-xs text-slate-500 capitalize">{exp.scope} Scope</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">
                      {new Date(exp.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">
                      {exp.rows.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">
                      {formatBytes(exp.size_bytes)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <a 
                          href={`/api/leaks/exports/${exp._id}/download`} 
                          target="_blank" rel="noreferrer"
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
                          title="Download"
                        >
                          <Download size={16} />
                        </a>
                        <button 
                          onClick={() => handleDelete(exp._id)}
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
}
